import { useState, useEffect, useRef, useMemo } from "react";
import { useOutletContext } from "react-router-dom";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid
} from "recharts";
import { useAuth } from "../context/AuthContext";
import { fetchDashboardData } from "../services/dashboardService";

// SVG Icons from assets/dashboard
import totalProductIcon from "../assets/dashboard/Totalproduct.svg";
import pendingReviewIcon from "../assets/dashboard/Pendingreview.svg";
import approvedIcon from "../assets/dashboard/Approved.svg";
import teamMembersIcon from "../assets/dashboard/Teammembers.svg";
import increasingIcon from "../assets/dashboard/increasing.svg";
import decreasingIcon from "../assets/dashboard/decreasing.svg";



/**
 * StatsCard — card with a step-down notch in the top-right corner.
 * The notch is drawn via SVG path; the icon box floats in that notch area.
 */
function StatsCard({ title, value, trend, desc, icon, trendIcon, trendColor, styleConfig, valueColor, descColor }) {
  const wrapRef = useRef(null);
  const [dims, setDims] = useState({ w: 0, h: 0 });

  useEffect(() => {
    if (!wrapRef.current) return;
    const ro = new ResizeObserver(([entry]) => {
      setDims({
        w: Math.floor(entry.contentRect.width),
        h: Math.floor(entry.contentRect.height),
      });
    });
    ro.observe(wrapRef.current);
    return () => ro.disconnect();
  }, []);

  const { w, h } = dims;

  // Notch & corner geometry
  const notchW = 76;  // total width reserved for the notch from right edge
  const notchH = 76;  // total height of the notch from top
  const R = 20;       // outer border-radius on the rest of the corners
  const Ri = 16;      // convex turn radius at the notch entry corners

  // SVG path: card body with a rectangular notch cut from top-right
  const path = w && h
    ? [
        `M ${R} 0`,
        `L ${w - notchW - Ri} 0`,
        `Q ${w - notchW} 0 ${w - notchW} ${Ri}`,
        `L ${w - notchW} ${notchH - Ri}`,
        `Q ${w - notchW} ${notchH} ${w - notchW + Ri} ${notchH}`,
        `L ${w - R} ${notchH}`,
        `Q ${w} ${notchH} ${w} ${notchH + R}`,
        `L ${w} ${h - R}`,
        `Q ${w} ${h} ${w - R} ${h}`,
        `L ${R} ${h}`,
        `Q 0 ${h} 0 ${h - R}`,
        `L 0 ${R}`,
        `Q 0 0 ${R} 0`,
        "Z",
      ].join(" ")
    : "";

  return (
    <div
      ref={wrapRef}
      className="relative transition-all duration-300 hover:-translate-y-1"
      style={{ minHeight: 178 }}
    >
      {/* SVG card shape */}
      {w > 0 && h > 0 && (
        <svg
          className="absolute inset-0"
          width={w}
          height={h}
          viewBox={`0 0 ${w} ${h}`}
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          style={{ filter: "drop-shadow(0px 8px 32px rgba(15,124,138,0.04))" }}
        >
          <path
            d={path}
            fill={styleConfig.background}
            stroke={styleConfig.borderColor}
            strokeWidth="1"
          />
        </svg>
      )}

      {/* Icon Box */}
      <div
        className="absolute z-20 flex items-center justify-center rounded-[14px]"
        style={{
          width: 58,
          height: 58,
          top: 9,
          right: 9,
          backgroundColor: styleConfig.iconBg,
          boxShadow: `0 4px 16px ${styleConfig.iconBg}55`,
        }}
      >
        <img src={icon} alt={title} className="w-[26px] h-[26px] object-contain" />
      </div>

      {/* Text Content */}
      <div
        className="relative z-10 flex flex-col"
        style={{
          padding: "22px 24px 20px 24px",
          minHeight: 178,
          paddingRight: notchW + 4,
        }}
      >
        {/* Label */}
        <p className="text-[10px] font-extrabold uppercase tracking-[0.13em] text-[#52606B]">
          {title}
        </p>

        {/* Big number */}
        <p className="text-[42px] font-extrabold leading-none mt-2" style={{ color: valueColor || "#081A3E" }}>
          {value}
        </p>

        {/* Trend & Desc */}
        {(trend || desc) && (
          <div className="mt-auto pt-5">
            {trend && (
              <div className="flex items-center gap-1.5">
                {/* {trendIcon && (
                  <img src={trendIcon} alt="trend" className="w-3 h-2.5 object-contain" />
                )} */}
                <span className="text-[12px] font-extrabold" style={{ color: trendColor }}>
                  {trend}
                </span>
              </div>
            )}
            {desc && (
              <div className="flex items-center gap-1.5 mt-1">
                <span className={descColor ? "text-[12px] font-extrabold" : "text-[11px] text-gray-400 font-medium italic"} style={descColor ? { color: descColor } : {}}>
                  {desc}
                </span>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default function DashboardPage() {
  const { user } = useAuth();
  const [selectedPoint, setSelectedPoint] = useState(null);

  // API query parameters
  const [timeRange, setTimeRange] = useState("monthly"); // "weekly", "monthly", "quarterly", "yearly"
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);

  useEffect(() => {
    let active = true;
    async function loadData() {
      setLoading(true);
      try {
        const res = await fetchDashboardData({
          range: timeRange,
          trendBasis: "same_period",
        });
        if (active && res.success) {
          setDashboardData(res.data);
        }
      } catch (err) {
        console.error("Failed to load dashboard statistics:", err);
      } finally {
        if (active) setLoading(false);
      }
    }
    loadData();
    return () => {
      active = false;
    };
  }, [timeRange]);

  const activeChartData = useMemo(() => {
    return dashboardData?.analytics?.cohort || [];
  }, [dashboardData]);

  // Initialize/update selectedPoint to show the latest period data by default
  useEffect(() => {
    if (activeChartData && activeChartData.length > 0) {
      setSelectedPoint(activeChartData[activeChartData.length - 1]);
    } else {
      setSelectedPoint(null);
    }
  }, [activeChartData]);

  // Map metrics objects safely from API envelope
  const metrics = dashboardData?.metrics || {};
  const tp = metrics.total_products || {};
  const pr = metrics.pending_review || {};
  const ap = metrics.approved || {};
  const tm = metrics.total_members || {};

  return (
    <div className="space-y-6 md:space-y-8 page-enter relative">

      {/* ── Section 1: Greeting + Metrics ── */}
      <div className="bg-white border border-[#0000001A] rounded-[20px] p-6 md:p-8 relative">
        {loading && (
          <div className="absolute inset-0 bg-white/40 backdrop-blur-[2px] z-30 flex items-center justify-center rounded-[20px]">
            <div className="w-8 h-8 border-2 border-[#0d5f66]/30 border-t-[#0d5f66] rounded-full animate-spin" />
          </div>
        )}

        <h1 className="text-2xl md:text-3xl font-extrabold text-[#081A3E]">
          Hello {user?.name ? user.name.split(" ")[0] : "Admin"}
        </h1>
        <p className="text-sm text-gray-400 font-medium mt-1 max-w-2xl">
          Overview of product submissions and team activity. Monitor performance in real-time.
        </p>

        {/* 4-column metrics grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-8">

          <StatsCard
            title="Total Products"
            value={tp.value ?? 0}
            trend={tp.trend?.label}
            trendColor={tp.trend?.direction === "down" ? "#BA1A1A" : "#059D56"}
            trendIcon={tp.trend?.direction === "down" ? decreasingIcon : increasingIcon}
            desc={tp.description || "In the database"}
            icon={totalProductIcon}
            styleConfig={{
              borderColor: "#FAB85F80",
              boxShadow: "0px 8px 32px 0px #0F7C8A0A",
              backdropFilter: "blur(20px)",
              background: "rgba(250,184,95,0.10)",
              iconBg: "#FAB85F",
            }}
          />

          <StatsCard
            title="Pending Review"
            value={pr.value ?? 0}
            trend={pr.trend?.label}
            trendColor={pr.trend?.direction === "down" ? "#BA1A1A" : "#059D56"}
            trendIcon={pr.trend?.direction === "down" ? decreasingIcon : increasingIcon}
            desc={pr.description || "Awaiting approval"}
            icon={pendingReviewIcon}
            valueColor={pr.value > 100 ? "#BA1A1A" : undefined}
            styleConfig={{
              borderColor: "#969EFA80",
              boxShadow: "0px 8px 32px 0px #0F7C8A0A",
              backdropFilter: "blur(20px)",
              background: "rgba(150,158,250,0.10)",
              iconBg: "#969EFA",
            }}
          />

          <StatsCard
            title="Approved"
            value={ap.value ?? 0}
            trend={ap.trend?.label || (ap.extra ? `Approved today: ${ap.extra.today_count}` : "")}
            trendColor={ap.trend?.direction === "down" ? "#BA1A1A" : "#059D56"}
            trendIcon={ap.trend?.direction === "down" ? decreasingIcon : increasingIcon}
            desc={ap.description || (ap.extra ? `${ap.extra.lifetime_total} approved till today` : "")}
            icon={approvedIcon}
            styleConfig={{
              borderColor: "#3AA1A380",
              boxShadow: "0px 8px 32px 0px #0F7C8A0A",
              backdropFilter: "blur(20px)",
              background: "rgba(58,161,163,0.10)",
              iconBg: "#37BEB0",
            }}
          />

          <StatsCard
            title="Total Members"
            value={tm.value ?? 0}
            trend={tm.extra ? `CRM: ${tm.extra.office_users_count + tm.extra.data_collection_agents_count} Users` : tm.trend?.label}
            trendColor="#0F7C8A"
            desc={tm.extra ? `Consumer App: ${tm.extra.consumer_users_count} Users` : tm.description}
            descColor={tm.extra ? "#0F7C8A" : undefined}
            icon={teamMembersIcon}
            trendIcon={increasingIcon}
            styleConfig={{
              borderColor: "#E26FB780",
              boxShadow: "0px 8px 32px 0px #0F7C8A0A",
              backdropFilter: "blur(20px)",
              background: "rgba(226,111,183,0.10)",
              iconBg: "#E26FB7",
            }}
          />

        </div>

      </div>

      {/* ── Section 2: Analytics ── */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 md:gap-8">

        {/* Approval Workflow Analytics - Full Width */}
        <div className="lg:col-span-12 bg-white border border-[#0000001A] rounded-[20px] p-6 flex flex-col">
          <div className="flex justify-between items-start mb-6 flex-wrap gap-2">
            <div>
              <h2 className="text-lg font-bold text-[#081A3E]">Approval Workflow Analytics</h2>
              <p className="text-xs text-gray-400 font-semibold mt-0.5">
                Product review &amp; validation statistics
              </p>
            </div>
            <div className="flex items-center bg-[#F0F2F5] p-1 rounded-full text-[10px] font-bold text-gray-400 gap-0.5">
              {["weekly", "monthly", "quarterly", "yearly"].map((rng) => (
                <button
                  key={rng}
                  onClick={() => setTimeRange(rng)}
                  className={`py-1 px-3.5 rounded-full transition-all capitalize ${
                    timeRange === rng ? "bg-white text-gray-800 shadow-sm" : "hover:text-gray-700"
                  }`}
                >
                  {rng.replace("ly", "")}
                </button>
              ))}
            </div>
          </div>

          {/* Selected Point Display (Stock Market style header) */}
          <div className="flex flex-wrap items-center gap-x-8 gap-y-2 mb-6 px-5 py-3.5 bg-[#0F7C8A05] border border-[#0F7C8A1A] rounded-2xl">
            <div className="min-w-[80px]">
              <span className="text-[10px] uppercase font-extrabold text-[#7A8C9B] tracking-[0.1em]">Selected Period</span>
              <p className="text-xl font-extrabold text-[#081A3E] leading-tight mt-0.5">
                {selectedPoint ? selectedPoint.label : "—"}
              </p>
            </div>
            <div className="h-8 w-[1px] bg-[#00000014] hidden sm:block" />
            <div className="min-w-[80px]">
              <span className="text-[10px] uppercase font-extrabold text-[#7A8C9B] tracking-[0.1em]">Total Products</span>
              <p className="text-xl font-extrabold text-[#0F7C8A] leading-tight mt-0.5">
                {selectedPoint ? selectedPoint.total : "—"}
              </p>
            </div>
            <div className="h-8 w-[1px] bg-[#00000014] hidden sm:block" />
            <div className="min-w-[80px]">
              <span className="text-[10px] uppercase font-extrabold text-[#7A8C9B] tracking-[0.1em]">Approved</span>
              <p className="text-xl font-extrabold text-[#37BEB0] leading-tight mt-0.5">
                {selectedPoint ? selectedPoint.approved : "—"}
              </p>
            </div>
            <div className="h-8 w-[1px] bg-[#00000014] hidden sm:block" />
            <div className="min-w-[80px]">
              <span className="text-[10px] uppercase font-extrabold text-[#7A8C9B] tracking-[0.1em]">Pending</span>
              <p className="text-xl font-extrabold text-[#E26FB7] leading-tight mt-0.5">
                {selectedPoint ? selectedPoint.pending : "—"}
              </p>
            </div>
          </div>

          <div className="h-[320px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart
                data={activeChartData}
                margin={{ top: 10, right: 10, left: -25, bottom: 0 }}
                onMouseMove={(state) => {
                  if (state.activePayload && state.activePayload.length > 0) {
                    setSelectedPoint(state.activePayload[0].payload);
                  }
                }}
                onClick={(state) => {
                  if (state.activePayload && state.activePayload.length > 0) {
                    setSelectedPoint(state.activePayload[0].payload);
                  }
                }}
                onMouseLeave={() => {
                  if (activeChartData && activeChartData.length > 0) {
                    setSelectedPoint(activeChartData[activeChartData.length - 1]);
                  }
                }}
              >
                <defs>
                  <linearGradient id="totalGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#0F7C8A" stopOpacity={0.15} />
                    <stop offset="100%" stopColor="#0F7C8A" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#0000000D" />
                <XAxis dataKey="label" tickLine={false} axisLine={false}
                  tick={{ fill: "#A0AEC0", fontSize: 10, fontWeight: "bold" }} dy={10}
                  minTickGap={40} />
                <YAxis tickLine={false} axisLine={false}
                  tick={{ fill: "#A0AEC0", fontSize: 10, fontWeight: "bold" }} dx={-10}
                  domain={[0, 'auto']} />
                <Tooltip
                  cursor={{ stroke: "#0F7C8A", strokeWidth: 1.5, strokeDasharray: "3 3" }}
                  contentStyle={{
                    background: "#fff",
                    border: "1px solid #0000001A",
                    borderRadius: 12,
                    fontSize: 11,
                    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.05)"
                  }}
                />
                <Area type="monotone" dataKey="total" name="Total" stroke="#0F7C8A"
                  strokeWidth={2.5} fill="url(#totalGrad)" dot={false} />
                <Area type="monotone" dataKey="approved" name="Approved" stroke="#37BEB0"
                  strokeWidth={1.5} strokeDasharray="5 4" fill="none" dot={false} />
                <Area type="monotone" dataKey="pending" name="Pending" stroke="#E26FB7"
                  strokeWidth={1.5} strokeDasharray="5 4" fill="none" dot={false} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

      </div>
    </div>
  );
}
